import React, { useCallback, useEffect, useState } from 'react';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import {
  decideDisputeRequest,
  fetchDisputeResolutionRequests
} from '../../services/customerServiceService';

const { FiCheck, FiLoader, FiRefreshCw, FiX } = FiIcons;

const AdminCustomerServiceRequestsPanel = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState('');
  const [error, setError] = useState('');

  const refresh = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setRequests(await fetchDisputeResolutionRequests() || []);
    } catch (requestError) {
      setError(requestError.message || 'Unable to load customer-service requests.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const decide = async (request, decision) => {
    const note = window.prompt(
      decision === 'approved' ? 'Add an approval note for the record.' : 'Explain why this request is being declined.'
    );
    if (note === null || note.trim().length < 3) return;
    setBusyId(request.id);
    setError('');
    try {
      await decideDisputeRequest(request.id, decision, note.trim());
      await refresh();
    } catch (requestError) {
      setError(requestError.message || 'Unable to record the decision.');
    } finally {
      setBusyId('');
    }
  };

  return (
    <section className="mt-8 space-y-5 rounded-[2.5rem] border border-gray-100 bg-white p-6 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-black text-gray-900">Customer Service Approval Queue</h2>
          <p className="mt-1 text-xs text-slate-500">Approval records a decision; it does not automatically process a refund.</p>
        </div>
        <button type="button" onClick={refresh} className="flex items-center gap-2 rounded-xl bg-indigo-50 px-4 py-3 text-[9px] font-black uppercase tracking-widest text-indigo-600">
          <SafeIcon icon={FiRefreshCw} /> Refresh
        </button>
      </div>
      {error && <p className="rounded-xl bg-rose-50 p-3 text-sm font-bold text-rose-700">{error}</p>}
      {loading ? <SafeIcon icon={FiLoader} className="animate-spin text-2xl text-indigo-500" /> : requests.length ? (
        <div className="space-y-3">
          {requests.map((request) => (
            <article key={request.id} className="rounded-2xl border border-slate-100 p-4">
              <div className="flex flex-wrap justify-between gap-2">
                <p className="font-black text-slate-900">Order #{request.order_id.slice(0, 8).toUpperCase()} · {request.recommendation.replaceAll('_', ' ')}</p>
                <span className={`text-[9px] font-black uppercase ${request.status === 'pending' ? 'text-amber-600' : request.status === 'approved' ? 'text-emerald-600' : 'text-rose-600'}`}>{request.status}</span>
              </div>
              <p className="mt-1 text-xs text-slate-500">Dispute: {request.reason.replaceAll('_', ' ')} · Rep: {request.rep_name}</p>
              {request.recommended_refund_cents > 0 && <p className="mt-2 text-xs font-black text-indigo-600">Suggested refund: ${(request.recommended_refund_cents / 100).toFixed(2)}</p>}
              <p className="mt-3 whitespace-pre-wrap text-sm text-slate-700">{request.message}</p>
              {request.decision_note && <p className="mt-2 text-xs text-slate-500">Decision note: {request.decision_note}</p>}
              {request.status === 'pending' && (
                <div className="mt-4 flex gap-2">
                  <button type="button" disabled={busyId === request.id} onClick={() => decide(request, 'approved')} className="flex items-center gap-1 rounded-xl bg-emerald-600 px-4 py-2 text-[9px] font-black uppercase text-white disabled:opacity-50">
                    <SafeIcon icon={FiCheck} /> Approve
                  </button>
                  <button type="button" disabled={busyId === request.id} onClick={() => decide(request, 'rejected')} className="flex items-center gap-1 rounded-xl bg-slate-100 px-4 py-2 text-[9px] font-black uppercase text-slate-600 disabled:opacity-50">
                    <SafeIcon icon={FiX} /> Decline
                  </button>
                </div>
              )}
            </article>
          ))}
        </div>
      ) : <p className="rounded-xl bg-slate-50 p-6 text-center text-sm text-slate-500">No customer-service requests yet.</p>}
    </section>
  );
};

export default AdminCustomerServiceRequestsPanel;
